// Video deeplink bootstrap — opens a family-stories video modal from a
// URL parameter on landing.
//
// Contract:
//   https://www.fintepla.com/lennox-gastaut-syndrome/family-stories?video=upp
//   https://www.fintepla.com/lennox-gastaut-syndrome/family-stories#video-upp
//
//   On DOMContentLoaded we read `?video=<slug>` (falling back to the
//   `#video-<slug>` hash), find the poster carrying
//   `data-video-slug="<slug>"` (rendered server-side by the Drupal CMS
//   — see docs/fintepla-cms-changes.md), and dispatch a click on it so
//   the Drupal modal opens in place.
//
// URL hygiene:
//   Before clicking, we call `history.replaceState(null, "", location.pathname)`
//   so the browser's back/forward stack does NOT contain the deeplink.
//   This is important on Drupal, where the modal is an in-page affordance
//   — walking back into the deeplink would re-open the modal every time.
//
// Race with late hydration:
//   `data-video-slug` is rendered server-side by Drupal, so it is
//   present in the initial HTML. However, Drupal behaviors can still
//   re-render or re-attach the family-stories cards after
//   DOMContentLoaded (lazy-loaded blocks, editorial reorder, AJAX
//   filter changes). We retry up to 10 times at 250 ms intervals
//   (2.5 s budget), then give up silently.
//
// Opt-in only:
//   No-op when `?video=` / `#video-` is absent. Does not interfere with
//   any existing on-page modal-open handler — we simply invoke
//   `.click()` on an element that already has its own listener bound
//   by Drupal's `video_embed_field` module.
//
// ES2018 only, zero external dependencies. Idempotent via
// `window.__finteplaVideoDeeplinkBootstrapInitialized`.

const INIT_FLAG = '__finteplaVideoDeeplinkBootstrapInitialized'
const QUERY_PARAM = 'video'
const HASH_PREFIX = 'video-'
const MAX_ATTEMPTS = 10
const RETRY_INTERVAL_MS = 250
// Slug is written by us (tagger) and therefore always URL-safe, but we
// validate defensively so a hostile ?video= value can't be used as a
// selector-injection vector before CSS.escape.
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-_]*$/i

function readSlugFromQuery() {
  try {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get(QUERY_PARAM)
    return raw ? raw.trim() : ''
  } catch {
    return ''
  }
}

function readSlugFromHash() {
  const raw = (window.location.hash || '').replace(/^#/, '')
  if (!raw.startsWith(HASH_PREFIX)) return ''
  return raw.slice(HASH_PREFIX.length).trim()
}

function resolveSlug() {
  return readSlugFromQuery() || readSlugFromHash()
}

function cleanUrl() {
  // Strip both the query string AND the hash so neither back/forward
  // navigation nor a soft refresh re-triggers the modal open. We use
  // `replaceState` (not `pushState`) so the deeplinked entry is
  // overwritten in place — the user's referrer / history depth is
  // preserved.
  try {
    window.history.replaceState(null, '', window.location.pathname)
  } catch {
    // Some exotic contexts (sandboxed iframes without allow-same-origin)
    // disallow history mutations. The deeplink still works; we just
    // leave the URL alone.
  }
}

function attemptClick(slug) {
  const selector = `[data-video-slug="${CSS.escape(slug)}"]`
  let target
  try {
    target = document.querySelector(selector)
  } catch {
    return false
  }
  if (!target) return false
  target.click()
  return true
}

function bootstrap(debug) {
  const slug = resolveSlug()
  if (!slug) return

  if (!SLUG_PATTERN.test(slug)) {
    if (debug) {
      console.info(
        '[Fintepla] Deeplink bootstrap: rejecting malformed slug',
        slug,
      )
    }
    return
  }

  // Clean the URL BEFORE clicking so a synchronous Drupal behavior that
  // reads `location.search` during its click handler doesn't see the
  // param and try to do its own thing.
  cleanUrl()

  if (attemptClick(slug)) {
    if (debug) {
      console.info(`[Fintepla] Deeplink bootstrap: opened "${slug}" on first try.`)
    }
    return
  }

  let attempts = 1
  const intervalId = window.setInterval(() => {
    attempts += 1
    if (attemptClick(slug)) {
      window.clearInterval(intervalId)
      if (debug) {
        console.info(
          `[Fintepla] Deeplink bootstrap: opened "${slug}" on attempt ${attempts}.`,
        )
      }
      return
    }
    if (attempts >= MAX_ATTEMPTS) {
      window.clearInterval(intervalId)
      if (debug) {
        console.info(
          `[Fintepla] Deeplink bootstrap: gave up opening "${slug}" after ${MAX_ATTEMPTS} attempts.`,
        )
      }
    }
  }, RETRY_INTERVAL_MS)
}

// Public entry point. Safe to call anywhere in the embed boot path;
// subsequent calls short-circuit.
export function finteplaVideoDeeplinkBootstrap({ debug = false } = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (window[INIT_FLAG]) return
  window[INIT_FLAG] = true

  const run = () => bootstrap(debug)

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true })
  } else {
    run()
  }
}

// Console/QA hook. Does NOT run on import.
if (typeof window !== 'undefined') {
  window.finteplaVideoDeeplinkBootstrap = finteplaVideoDeeplinkBootstrap
}

